import { Grid, GridColumn, GridRow, TabBar } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 } from 'uuid';
import { useDeputateLessons } from '../../../../components/Lessons/hooks/useDeputateLessons';
import { DeputateLessonsTable } from '../../../../components/Lessons/Tables/DeputateLessonsTable';
import { LessonStatsTable } from '../../../../components/Lessons/Tables/LessonStatsTable';
import { Page } from '../../../../components/Page/Page';
import { useAuthClaims } from '../../../../hooks/useAuthClaims';
import { useLoadBasicData } from '../../../../hooks/useLoadBasicData';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../../../hooks/useSelectedSchoolYear';
import { useClassesListQuery } from '../../../../types/planung-graphql-client-defs';
import { hexToColorOption } from '../../../../utils/colorUtils';
import { LessonTabsType } from './LessonsPage';

export const LessonClassDetailSubpage = observer(() => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const schoolYear = useSelectedSchoolYear();
  const { classUuid } = useParams();
  const permissions = usePermissionChecker();

  const { lessons: lessonsData } = useDeputateLessons({
    organizationUuid: pimAuthClaims.getOrganizationUuid(),
    schoolYearUuid: schoolYear?.uuid ?? '',
  });

  const filteredLessons = lessonsData
    .sort((a, b) => {
      if (a.subject < b.subject) return -1;
      if (a.subject > b.subject) return 1;

      const aTeacherSort = a.teacherSort || '';
      const bTeacherSort = b.teacherSort || '';

      if (aTeacherSort < bTeacherSort) return -1;
      if (aTeacherSort > bTeacherSort) return 1;
      return 0;
    })
    .filter((lesson) => {
      return lesson.classes.some((c) => c.uuid === classUuid);
    });

  const assignedData = filteredLessons.filter((l) => l.teachers.length > 0 && !l.isSubjectContainerLesson);
  const unassignedData = filteredLessons.filter((l) => l.teachers.length === 0 && !l.isSubjectContainerLesson);
  const subjectContainerLessons = filteredLessons.filter((l) => l.isSubjectContainerLesson);

  const { classesData } = useLoadBasicData({ pause: !schoolYear });

  const currentClass = classesData?.classes.find((c) => c.uuid === classUuid);

  const { html, label } = hexToColorOption(currentClass?.timetableConfig?.color ?? '');

  const defaultInitialValues = {
    teachingLoadEnabled: true,
    lessonClasses: [
      {
        uuid: v4(),
        groups: [],
        class: {
          uuid: currentClass?.uuid ?? '',
        },
      },
    ],
  };

  const tabs: LessonTabsType[] = [
    {
      trigger: t('lesson.all'),
      id: 'all',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='all'
          configKey='lessons-table-classes-all'
          lessonsData={filteredLessons.filter((l) => !l.isSubjectContainerLesson) ?? []}
          classUuid={currentClass?.uuid}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
    {
      trigger: t('lesson.assigned'),
      id: 'assigned',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='assigned'
          configKey='lessons-table-classes-assigned'
          lessonsData={assignedData ?? []}
          classUuid={currentClass?.uuid}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
    {
      trigger: t('lesson.unassigned'),
      id: 'unassigned',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='unassigned'
          configKey='lessons-table-classes-unassigned'
          lessonsData={unassignedData ?? []}
          classUuid={currentClass?.uuid}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
    {
      trigger: t('teachingBlockSubjectContainer.title', { count: 2 }),
      id: 'subjectContainerLessons',
      content: (
        <DeputateLessonsTable
          lessonTabFilterType='subjectContainerLessons'
          configKey='lessons-table-classes-subjectContainerLessons'
          lessonsData={
            subjectContainerLessons.sort((scl1, scl2) => {
              const class1 = scl1.classes[0];
              const class2 = scl2.classes[0];
              return (
                scl1.subject.localeCompare(scl2.subject) ||
                (class1?.grade ?? 0) - (class2?.grade ?? 0) ||
                (class1?.gradeGroup?.name ?? '').localeCompare(class2?.gradeGroup?.name ?? '')
              );
            }) ?? []
          }
          classUuid={currentClass?.uuid}
          defaultInitialValues={defaultInitialValues}
        />
      ),
    },
  ];

  return (
    <Page
      isForbidden={!permissions?.canReadDeputate()}
      title={currentClass?.name}
      colorHint={{ color: html, colorLabel: label }}
    >
      <Grid>
        <GridRow>
          <GridColumn width={4} offset='right'>
            <LessonStatsTable lessonsData={filteredLessons} />
          </GridColumn>
        </GridRow>
        <GridRow>
          <div style={{ display: 'block', width: '100%' }}>
            <TabBar tabs={tabs} />
          </div>
        </GridRow>
      </Grid>
    </Page>
  );
});
